import { useEffect, useState } from 'react';
import { callApi } from '../lib/api.ts';
import { friendlyError } from '../lib/friendlyError.ts';
import { btnSolid, input as inputCls, card, sectionTitle, sectionDesc } from '../lib/ui.ts';
import { FlapText } from './FlapText.tsx';

interface DesignerRow {
  email: string;
  status: 'invited' | 'active';
}

/**
 * #28 — admin-only in-app DESIGNER onboarding (scaling-plan §3, Door 1). Invite a designer
 * to the cockpit by email, and see who's been invited + who's signed in. Client assignment
 * stays in the Clients panel above; this only mints the login (role=designer via the
 * handle_new_user trigger). Go-live/retire/analytics remain admin.
 */
export function DesignersPanel() {
  const [designers, setDesigners] = useState<DesignerRow[]>([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function loadList() {
    try {
      const { designers } = await callApi<{ designers: DesignerRow[] }>('invite-designer', {
        method: 'POST',
        body: JSON.stringify({ list: true }),
      });
      setDesigners(designers);
    } catch {
      /* non-fatal: the list is a convenience; inviting still works */
    }
  }

  useEffect(() => {
    void loadList();
  }, []);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await callApi<{ email: string }>('invite-designer', {
        method: 'POST',
        body: JSON.stringify({ invite: email }),
      });
      setNotice(`Invite sent to ${res.email}. They'll get an email to set a password and sign in.`);
      setEmail('');
      await loadList();
    } catch (err) {
      setError(friendlyError(err as Error, (err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={`${card} space-y-5 p-5`}>
      <div>
        <h2 className={sectionTitle}>Designers</h2>
        <p className={sectionDesc}>
          Invite a designer to the cockpit. They get a login scoped to the clients you assign
          (assign them in Clients, above). Go-live, retire and analytics stay with you.
        </p>
      </div>

      <form onSubmit={invite} className="flex flex-wrap items-center gap-2">
        <input
          type="email"
          required
          placeholder="designer@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`min-w-[16rem] flex-1 ${inputCls}`}
        />
        <button type="submit" disabled={busy || !email} className={btnSolid}>
          <FlapText>{busy ? 'Inviting…' : 'Invite'}</FlapText>
        </button>
      </form>

      {error && <p className="text-sm text-danger">{error}</p>}
      {notice && <p className="text-sm text-success">{notice}</p>}

      {designers.length > 0 && (
        <ul className="divide-y divide-line text-sm">
          {designers.map((d) => (
            <li key={d.email} className="flex items-center justify-between py-1.5">
              <span className="text-ink">{d.email}</span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs ${
                  d.status === 'active'
                    ? 'bg-paper-2 text-ink-2'
                    : 'bg-warning-bright/15 text-warning'
                }`}
              >
                {d.status === 'active' ? 'Active' : 'Invited'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
